import React from 'react';

// Helper function to parse time string from database
function parseTimeString(timeString) {
  if (!timeString || timeString === 'N/A' || timeString === null) return null;
  
  // Handle the format "1900-01-01 15:00:00" from CSV data
  const date = new Date(timeString);
  
  // Check if the date is valid
  if (isNaN(date.getTime())) return null;
  
  return date;
}

// Helper to format time for display
function formatTime(date) {
  if (!date) return '';
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

// Helper to group days by time slots
function groupDaysByTime(days, startTimes, endTimes) {
  const dayTimeMap = days.map((day, i) => ({
    day,
    startTime: startTimes[i],
    endTime: endTimes[i],
    timeSignature:
      startTimes[i] && endTimes[i]
        ? `${startTimes[i]?.getTime()}-${endTimes[i]?.getTime()}`
        : null,
  }));

  const timeGroups = {};

  dayTimeMap.forEach(({ day, startTime, endTime, timeSignature }) => {
    if (timeSignature && startTime && endTime) {
      if (!timeGroups[timeSignature]) {
        timeGroups[timeSignature] = {
          days: [],
          startTime,
          endTime,
        };
      }
      timeGroups[timeSignature].days.push(day);
    }
  });

  return Object.values(timeGroups);
}

export default function DaySchedule({ days, startTimes, endTimes }) {
  if (!startTimes || !endTimes) {
    return (
      <div className="text-xs text-gray-400 dark:text-dark-500 italic text-center">
        No scheduled classes
      </div>
    );
  }

  // Parse days from JSON string if needed
  let parsedDays = days || [];
  if (typeof days === 'string') {
    try {
      parsedDays = JSON.parse(days);
    } catch (e) {
      parsedDays = [];
    }
  }

  // Parse JSON strings if needed for times
  let parsedStartTimes = startTimes;
  let parsedEndTimes = endTimes;

  if (typeof startTimes === 'string') {
    try {
      parsedStartTimes = JSON.parse(startTimes);
    } catch (e) {
      parsedStartTimes = [];
    }
  }

  if (typeof endTimes === 'string') {
    try {
      parsedEndTimes = JSON.parse(endTimes);
    } catch (e) {
      parsedEndTimes = [];
    }
  }

  // If no days specified, return no scheduled classes
  if (!parsedDays || parsedDays.length === 0) {
    return (
      <div className="text-xs text-gray-400 dark:text-dark-500 italic text-center">
        No scheduled classes
      </div>
    );
  }

  // Use actual days from course data
  const allDays = ['M', 'T', 'W', 'Th', 'F'];
  const activeDays = new Set(parsedDays);

  // Map the days to their proper indices in the 7-day array
  // [Sunday, Monday, Tuesday, Wednesday, Thursday, Friday, Saturday]
  const weekdayStartTimes = [];
  const weekdayEndTimes = [];

  for (let i = 0; i < 5; i++) {
    const sourceIndex = i + 1; // Monday=1, Tuesday=2, etc.

    if (parsedStartTimes && parsedStartTimes[sourceIndex]) {
      weekdayStartTimes[i] = parsedStartTimes[sourceIndex] === 'N/A' ? null : parsedStartTimes[sourceIndex];
    } else {
      weekdayStartTimes[i] = null;
    }

    if (parsedEndTimes && parsedEndTimes[sourceIndex]) {
      weekdayEndTimes[i] = parsedEndTimes[sourceIndex] === 'N/A' ? null : parsedEndTimes[sourceIndex];
    } else {
      weekdayEndTimes[i] = null;
    }
  }

  // Create processed data - show all weekdays, highlight active ones
  const processedDays = allDays.map((day, index) => {
    const hasClass = activeDays.has(day);
    return {
      day,
      hasClass,
      startTime: hasClass ? parseTimeString(weekdayStartTimes[index]) : null,
      endTime: hasClass ? parseTimeString(weekdayEndTimes[index]) : null,
    };
  });

  // Group days by time
  const processedStartTimes = processedDays.map((d) => d.startTime);
  const processedEndTimes = processedDays.map((d) => d.endTime);
  const timeGroups = groupDaysByTime(allDays, processedStartTimes, processedEndTimes);

  // Check if any days have classes
  const hasSomeClasses = processedDays.some((d) => d.hasClass);
  if (!hasSomeClasses) {
    return (
      <div className="text-xs text-gray-400 dark:text-dark-500 italic text-center">
        No scheduled classes
      </div>
    );
  }

  // Check if all class times are the same
  const allSameTime = timeGroups.length === 1;

  return (
    <div className="flex flex-col items-center space-y-2 max-w-[180px]">
      {/* Day indicators */}
      <div className="flex space-x-1">
        {processedDays.map(({ day, hasClass }, index) => (
          <div
            key={index}
            className={`w-6 h-6 flex items-center justify-center rounded text-xs font-medium ${
              hasClass
                ? 'bg-accent-primary text-white'
                : 'bg-gray-100 dark:bg-dark-300 text-gray-400 dark:text-dark-500'
            }`}
            title={
              hasClass && processedDays[index].startTime && processedDays[index].endTime
                ? `${day}: ${formatTime(processedDays[index].startTime)} - ${formatTime(
                    processedDays[index].endTime
                  )}`
                : `${day}: No class`
            }
          >
            {day}
          </div>
        ))}
      </div>

      {/* Time display */}
      <div className="text-xs text-gray-600 dark:text-dark-600 text-center space-y-0.5">
        {allSameTime && timeGroups[0] ? (
          <div className="font-medium">
            {formatTime(timeGroups[0].startTime)} - {formatTime(timeGroups[0].endTime)}
          </div>
        ) : (
          timeGroups.map((group, i) => (
            <div key={i} className="text-center">
              <span className="font-medium">{group.days.join(', ')}:</span>{' '}
              {formatTime(group.startTime)} - {formatTime(group.endTime)}
            </div>
          ))
        )}
      </div>
    </div>
  );
}